/**
 * Which assistant surface owns the conversation right now.
 *
 * The assistant has three input surfaces — the talking avatar's ambient mic,
 * voice mode (the waveform under the avatar), and the typed chat panel — and
 * they used to run independently. After login the avatar speaks its welcome
 * and then opens its mic on its own; if the shopper had meanwhile started
 * voice mode or opened the chat, two microphones were live and two turns
 * raced each other on one socket. This module is the one place that says
 * which surface is active, so the others can stand down.
 *
 * Rules:
 *   - Exactly one mode is active: "none" | "avatar" | "voice" | "chat".
 *   - The shopper's latest action wins. Starting voice mode, opening the chat,
 *     typing a message or tapping the avatar's mic each claims the mode.
 *   - The avatar may open its mic *by itself* only while nothing else is
 *     active ("none", the post-login welcome) or it already owns the turn
 *     ("avatar"). Once another surface has claimed the conversation, the
 *     avatar's loop stays off until the shopper taps its mic by hand.
 *   - In voice mode the avatar must neither play nor speak — the avatar is
 *     hidden there, and its audio would otherwise talk over the voice reply.
 *   - In chat mode the avatar stays on screen but must not be heard: the
 *     shopper is reading the thread, where the same reply arrives as text.
 *     A reply already being spoken, or one still on its way from the
 *     backend, plays out silently. The sound returns when the avatar owns
 *     the conversation again (its mic is tapped) or the panel closes.
 *
 * Framework-free (module state + subscribers, like toastManager) because the
 * callers are two components that hold no ref to each other, plus the WebRTC
 * service. State is in memory only: a reload starts at "none", and the chat
 * panel re-registers itself on mount.
 */

/** @typedef {"none" | "avatar" | "voice" | "chat"} AssistantMode */

const MODES = new Set(["none", "avatar", "voice", "chat"]);

/** @type {AssistantMode} */
let active = "none";
/** The chat panel is open — the mode voice/avatar fall back to when released. */
let chatOpen = false;
/** @type {Array<(change: { mode: AssistantMode, prev: AssistantMode, reason: string }) => void>} */
let subscribers = [];

function publish(prev, reason) {
  if (prev === active) return;
  const change = { mode: active, prev, reason };
  if (process.env.NODE_ENV !== "production") {
    console.debug("[AssistantMode]", `${prev} → ${active}`, reason);
  }
  subscribers.slice().forEach((cb) => {
    try {
      cb(change);
    } catch (err) {
      console.warn("[AssistantMode] subscriber threw", err);
    }
  });
}

/** The mode to rest in when nobody holds the conversation. */
function restingMode() {
  return chatOpen ? "chat" : "none";
}

/** @returns {AssistantMode} */
export function getAssistantMode() {
  return active;
}

/**
 * Subscribe to mode changes. The callback gets `{ mode, prev, reason }` and
 * is not called for no-op claims.
 * @returns {() => void} unsubscribe
 */
export function subscribeAssistantMode(cb) {
  if (typeof cb !== "function") return () => {};
  subscribers.push(cb);
  return () => {
    subscribers = subscribers.filter((s) => s !== cb);
  };
}

/**
 * A surface takes the conversation: "voice" when voice mode starts, "avatar"
 * when the avatar's mic opens, "chat" when the shopper types a message.
 * Claiming "chat" also marks the panel open (you can only type in an open one).
 * @param {AssistantMode} mode
 * @param {string} [reason] for the debug log
 */
export function claimAssistantMode(mode, reason = "claim") {
  if (!MODES.has(mode) || mode === "none") return;
  if (mode === "chat") chatOpen = true;
  const prev = active;
  active = mode;
  publish(prev, reason);
}

/**
 * A surface gives the conversation back — but only if it still holds it. A
 * stale release (voice mode ending after the avatar was tapped) must not
 * knock out the surface that took over.
 * @param {AssistantMode} mode
 * @param {string} [reason]
 */
export function releaseAssistantMode(mode, reason = "release") {
  if (active !== mode) return;
  const prev = active;
  active = restingMode();
  publish(prev, reason);
}

/**
 * The chat panel opened or closed. Opening takes the conversation from the
 * avatar (its ambient mic must not keep listening beside a typed thread) but
 * not from voice mode, whose transcript the panel is showing. Closing only
 * matters if the panel was the active surface.
 * @param {boolean} open
 */
export function setAssistantChatOpen(open) {
  chatOpen = !!open;
  const prev = active;
  if (chatOpen) {
    if (active !== "voice") active = "chat";
  } else if (active === "chat") {
    active = "none";
  }
  publish(prev, chatOpen ? "chat-open" : "chat-close");
}

/**
 * May the avatar open its mic without a tap? Only while nothing else holds
 * the conversation or the avatar already does. Every automatic re-open in
 * the avatar loop asks this, so one answer covers them all.
 */
export function isAvatarMicAutoStartAllowed() {
  return active === "none" || active === "avatar";
}

/**
 * Must the avatar stay silent and unplayed? True in voice mode: the avatar
 * is hidden there and its stream must not attach, play or unmute.
 */
export function isAvatarPlaybackSuppressed() {
  return active === "voice";
}

/**
 * Must the avatar be inaudible? True whenever another surface holds the
 * conversation. In chat mode this is the whole of the avatar's silence — it
 * keeps moving, muted. In voice mode the stream is detached as well (above);
 * the mute there only covers the hidden idle loop.
 *
 * This overrides the shopper's speaker preference without changing it, so
 * the preference is back as they left it when the mode moves on. Every
 * write to the avatar element's `muted` must OR this in; one that does not
 * lets the avatar talk over the chat.
 */
export function isAvatarAudioMuted() {
  return active === "chat" || active === "voice";
}

/** Test hook: back to a fresh page. */
export function _resetAssistantModeForTests() {
  active = "none";
  chatOpen = false;
  subscribers = [];
}
